/** Camera, recorder and wake-lock helpers for the teleprompter. */

/**
 * Constraints in sensor (landscape) terms. iOS Safari ignores a portrait request (1080x1920) and delivers a
 * 1920x1080 crop of the preview, so a phone held upright recorded a zoomed landscape take. With landscape numbers
 * Safari rotates the picture to how the phone is held (WebKit bug 323550, Apple forums 844826).
 */
export const CAMERA_CONSTRAINTS: MediaStreamConstraints = {
  video: { facingMode: 'user', width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } },
  audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
};

/** Prefers MP4 (Safari, newer Chrome), else WebM with VP9 and Opus, else the browser default. */
export function pickMimeType(): string {
  const supported = (t: string) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported?.(t);
  // H.264 High first: Safari reports Baseline as supported too and picks it for plain video/mp4 (soft picture)
  if (supported('video/mp4;codecs=avc1.640028,mp4a.40.2')) return 'video/mp4;codecs=avc1.640028,mp4a.40.2';
  if (supported('video/mp4')) return 'video/mp4';
  if (supported('video/webm;codecs=vp9,opus')) return 'video/webm;codecs=vp9,opus';
  if (supported('video/webm')) return 'video/webm';
  return '';
}

/** The content type for the upload route: the base type without codec parameters. */
export function baseContentType(mime: string): string {
  return mime.split(';')[0]?.trim() || 'video/webm';
}

export function extensionFor(contentType: string): string {
  return contentType.includes('mp4') ? 'mp4' : 'webm';
}

export function describeMediaError(e: unknown): string {
  const name = (e as { name?: string } | null)?.name;
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Camera and microphone are blocked. Allow access for this site in your browser settings, then open the teleprompter again.';
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError')
    return 'No camera or microphone found on this device.';
  if (name === 'NotReadableError') return 'The camera is in use by another app. Close it and try again.';
  return 'The camera could not be started. Try again or use another browser.';
}

export async function openCamera(facing: 'user' | 'environment' = 'user'): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw Object.assign(new Error('getUserMedia is not available'), { name: 'NotFoundError' });
  }
  if (facing === 'user') return navigator.mediaDevices.getUserMedia(CAMERA_CONSTRAINTS);
  return navigator.mediaDevices.getUserMedia({
    ...CAMERA_CONSTRAINTS,
    video: { ...(CAMERA_CONSTRAINTS.video as MediaTrackConstraints), facingMode: facing },
  });
}

export function stopStream(stream: MediaStream | null | undefined): void {
  stream?.getTracks().forEach((t) => t.stop());
}

interface WakeLockSentinelLike {
  release(): Promise<void>;
}

/** Keeps the screen on while reading. Silently does nothing where unsupported or refused. */
export async function requestWakeLock(): Promise<WakeLockSentinelLike | null> {
  try {
    const wl = (navigator as Navigator & { wakeLock?: { request(type: 'screen'): Promise<WakeLockSentinelLike> } })
      .wakeLock;
    return wl ? await wl.request('screen') : null;
  } catch {
    return null;
  }
}

/** Waits until the element shows a first frame (its size is the picture as displayed), at most `ms`. */
export function waitForFrame(video: HTMLVideoElement, ms = 2000): Promise<void> {
  return new Promise((resolve) => {
    const done = () => resolve();
    const timer = setTimeout(done, ms);
    const check = () => {
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        clearTimeout(timer);
        // the size can still flip once the camera delivers its first real frame (getSettings settles after ~450 ms)
        setTimeout(done, 500);
      } else requestAnimationFrame(check);
    };
    check();
  });
}

export interface RecordSource {
  stream: MediaStream;
  /** What gets recorded, for diagnostics: "track" or "canvas", with the size. */
  mode: string;
  stop(): void;
}

/** Draws a part of the shown picture into a canvas each frame and records that canvas with the microphone. */
function canvasSource(
  camera: MediaStream,
  video: HTMLVideoElement,
  crop: { sx: number; sw: number; sh: number },
  w: number,
  h: number,
  mode: string,
): RecordSource {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  let raf = 0;
  const draw = () => {
    ctx?.drawImage(video, crop.sx, 0, crop.sw, crop.sh, 0, 0, w, h);
    raf = requestAnimationFrame(draw);
  };
  draw();
  const out = (canvas as HTMLCanvasElement & { captureStream(fps?: number): MediaStream }).captureStream(30);
  camera.getAudioTracks().forEach((a) => out.addTrack(a));
  return {
    stream: out,
    mode,
    stop: () => {
      cancelAnimationFrame(raf);
      out.getVideoTracks().forEach((t) => t.stop());
    },
  };
}

const even = (n: number) => Math.round(n / 2) * 2;

/**
 * The stream to record. Normally the camera track itself. Two cases go through a canvas:
 * - the picture is upright portrait but the track reports landscape (iOS 26 records such tracks sideways and without
 *   a rotation flag): the shown picture at its own size;
 * - the camera delivers landscape on a phone held upright (seen on iPhones): the 9:16 middle of the picture, exactly
 *   what the full-screen preview shows. A laptop webcam (landscape window) is recorded as is.
 */
export function recordSource(
  camera: MediaStream,
  video: HTMLVideoElement,
  portraitScreen = typeof window !== 'undefined' && window.innerHeight > window.innerWidth,
): RecordSource {
  const track = camera.getVideoTracks()[0];
  const set = track?.getSettings?.() ?? {};
  const tw = set.width ?? 0;
  const th = set.height ?? 0;
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  const canvasOk = typeof HTMLCanvasElement !== 'undefined' && 'captureStream' in HTMLCanvasElement.prototype;
  const plain = { stream: camera, mode: `track ${tw}x${th}`, stop: () => {} };
  if (!canvasOk || !vw || !vh) return plain;
  if (vh > vw && tw > th) {
    const scale = Math.min(1, 1920 / vh);
    const w = even(vw * scale);
    const h = even(vh * scale);
    return canvasSource(camera, video, { sx: 0, sw: vw, sh: vh }, w, h, `canvas ${w}x${h} (track ${tw}x${th})`);
  }
  if (vw > vh && portraitScreen) {
    const sw = Math.round((vh * 9) / 16);
    const h = even(Math.min(1920, vh));
    const w = even((h * 9) / 16);
    return canvasSource(
      camera,
      video,
      { sx: Math.round((vw - sw) / 2), sw, sh: vh },
      w,
      h,
      `canvas crop ${w}x${h} (picture ${vw}x${vh})`,
    );
  }
  return plain;
}
